/**
 * DocusignSettings — Admin-Einstellungen → DocuSign (FA-010).
 *
 * Bisher waren die DocuSign-Vorlagen nur im Aktions-Editor der PB/BK-Stages
 * erreichbar (Auswahl-Dropdown). Diese Sektion macht sie eigenständig sichtbar:
 * welche Vorlagen im verbundenen DocuSign-Konto liegen, welche Rollen sie
 * haben und welche Datenfelder (tabLabels) je Rolle gefüllt werden. Genau
 * diese Feldnamen entscheiden, welche Profilwerte beim Versand ankommen —
 * ohne diese Ansicht muss man sie im DocuSign-Konto nachsehen.
 *
 * Quelle: GET /v2/admin/docusign/templates (Antwort wird serverseitig
 * zwischengespeichert, „Aktualisieren" ruft mit ?fresh=1 neu ab) und
 * GET /v2/admin/docusign/recipient-types für die Empfänger-Variablen.
 *
 * Nur Lesezugriff: Vorlagen werden in DocuSign gepflegt, nicht im CRM.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Badge } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { formatDateTime } from '../../lib/dateFormat';
import { v2Docusign, docusignErrorKind } from '../../lib/v1';
import type { DocusignTemplate, DocusignRecipientType, DocusignErrorKind } from '../../lib/v1';

interface DocusignSettingsProps {
  dark: boolean;
}

/** Feldnamen der Vorlage als Chips — das ist der eigentliche Informationswert. */
function FieldChips({ fields, dark }: { fields: string[]; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  if (fields.length === 0) {
    return <span style={{ fontSize: 11, color: t.muted }}>{tr('settings.docusign.no_fields')}</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {fields.map((f, i) => (
        <code
          key={`${f}-${i}`}
          style={{
            fontSize: 11, padding: '2px 7px', borderRadius: 6,
            border: `1px solid ${t.border}`, background: t.inputBg, color: t.text,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        >
          {f}
        </code>
      ))}
    </div>
  );
}

export function DocusignSettings({ dark }: DocusignSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [templates, setTemplates] = useState<DocusignTemplate[]>([]);
  const [recipientTypes, setRecipientTypes] = useState<DocusignRecipientType[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<DocusignErrorKind | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // Laden kann sich überholen (Doppelklick auf „Aktualisieren") — nur die
  // jüngste Antwort darf den State setzen, wie in den Nachbar-Sektionen.
  const loadSeq = useRef(0);
  const copyTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
  }, []);

  const load = async (fresh = false) => {
    const seq = ++loadSeq.current;
    const current = () => seq === loadSeq.current;

    setLoading(true);
    setErrorKind(null);
    try {
      const tpls = await v2Docusign.templates(fresh);
      if (!current()) return;
      setTemplates(tpls);
      // Empfänger-Variablen sind nur Zusatzinfo — ein Fehler hier darf die
      // Vorlagenliste nicht verstecken.
      try {
        const types = await v2Docusign.recipientTypes();
        if (current()) setRecipientTypes(types);
      } catch {
        if (current()) setRecipientTypes([]);
      }
    } catch (e) {
      if (!current()) return;
      setErrorKind(docusignErrorKind(e));
      setTemplates([]);
    } finally {
      if (current()) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyId = async (id: string) => {
    // navigator.clipboard ist secure-context-gated. Ohne API gar keine
    // Rückmeldung — sonst stünde „Kopiert" da, obwohl nichts kopiert wurde.
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopied(id);
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(c => (c === id ? null : c)), 1500);
    } catch {
      /* Clipboard nicht verfügbar (z. B. ohne HTTPS) — ID steht ohnehin da. */
    }
  };

  return (
    <Card dark={dark} style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle dark={dark} title={tr('settings.docusign.title')} subtitle={tr('settings.docusign.subtitle')} />
        <Button dark={dark} variant="secondary" size="sm" onClick={() => void load(true)} disabled={loading}>
          {tr('settings.docusign.refresh')}
        </Button>
      </div>

      {loading && templates.length === 0 && (
        <div style={{ fontSize: 13, color: t.muted, padding: '10px 0' }}>{tr('settings.docusign.loading')}</div>
      )}

      {!loading && errorKind && (
        <div
          role="alert"
          style={{
            fontSize: 13, color: '#b42318', background: dark ? '#3a1a17' : '#fef3f2',
            border: '1px solid #fda29b', borderRadius: 9, padding: '10px 12px', marginTop: 10,
          }}
        >
          {tr(`settings.docusign.error.${errorKind}`)}
        </div>
      )}

      {!loading && !errorKind && templates.length === 0 && (
        <div style={{ fontSize: 13, color: t.muted, padding: '10px 0' }}>{tr('settings.docusign.empty')}</div>
      )}

      {!errorKind && templates.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {templates.map(tpl => (
            <div
              key={tpl.template_id}
              style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: 14 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: t.heading }}>{tpl.name}</span>
                <Badge dark={dark} tone="neutral">
                  {tr('settings.docusign.role_count', { count: tpl.roles.length })}
                </Badge>
                <span style={{ fontSize: 11, color: t.muted, marginLeft: 'auto' }}>
                  {tr('settings.docusign.updated_at')}: {tpl.updated_at ? formatDateTime(tpl.updated_at, tpl.updated_at) : '—'}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <code
                  style={{
                    fontSize: 11, color: t.muted,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  {tpl.template_id}
                </code>
                <button
                  type="button"
                  onClick={() => void copyId(tpl.template_id)}
                  aria-label={copied === tpl.template_id ? tr('settings.docusign.copied') : tr('settings.docusign.copy_id')}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: 11, color: copied === tpl.template_id ? '#067647' : t.muted,
                    fontFamily: 'Outfit, sans-serif', padding: 0,
                  }}
                >
                  {copied === tpl.template_id ? tr('settings.docusign.copied') : tr('settings.docusign.copy_id')}
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                {tpl.roles.map(r => (
                  <div key={r.role}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: t.heading, marginBottom: 4 }}>
                      {r.role}
                      <span style={{ fontWeight: 400, color: t.muted }}>
                        {' '}· {tr('settings.docusign.routing_order')} {r.routing_order}
                      </span>
                    </div>
                    <FieldChips fields={r.fields} dark={dark} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!errorKind && recipientTypes.length > 0 && (
        <div style={{ marginTop: 18, borderTop: `1px solid ${t.border}`, paddingTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.heading, marginBottom: 6 }}>
            {tr('settings.docusign.recipients_title')}
          </div>
          <div style={{ fontSize: 11, color: t.muted, marginBottom: 8 }}>
            {tr('settings.docusign.recipients_hint')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {recipientTypes.map(r => (
              <Badge key={r.value} dark={dark} tone={r.group === 'intern' ? 'warning' : 'neutral'}>
                {r.group === 'familienkonto' ? `${tr('settings.docusign.contact_type')}: ${r.label}` : r.label}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
